<template>
  <section
    :class="['pricing', (shoppingCart.length >= 2 || isPromoAvailable) && 'pricing--populated']"
    id="pricing"
  >
    <div class="pricing__title-wrapper">
      <h3 class="pricing__title">Cart</h3>
    </div>
    <div class="pricing__breakdown">
      <div v-if="subscriptionsInCart.length" class="pricing__products-section">
        <p class="pricing__type">Subscriptions</p>
        <PricingListItem
          v-for="product in subscriptionsInCart"
          :key="product.id"
          v-bind="{ product, currencySymbol }"
          @remove-product="removeSelectedProduct"
        />
      </div>
      <div v-if="certificationsList.length" class="pricing__products-section--certifications">
        <PricingListItem
          v-for="product in certificationsList"
          :key="product.id"
          v-bind="{ product, currencySymbol }"
          @remove-product="removeSelectedProduct"
        />
      </div>
      <div v-if="bootcampsInCart.length" class="pricing__products-section">
        <p class="pricing__type">Bootcamps</p>
        <PricingListItem
          v-for="product in bootcampsInCart"
          :key="product.id"
          v-bind="{ product, currencySymbol }"
          @remove-product="removeSelectedProduct"
        />
      </div>
      <AddOns
        v-if="certificationsInAddOns.length"
        :certifications="certificationsInAddOns"
        :selectedPlanCode="selectedPlanCode"
        :isFreeTrial="isFreeTrial"
      />
      <div class="pricing__prices-wrapper">
        <p class="pricing__type">Promo code</p>
        <span v-if="showCouponAppliedPill" class="pricing__coupon-pill-wrapper pill pill--applied"
          >COUPON APPLIED</span
        >
        <form @submit.prevent="setupPricing" class="pricing__promo">
          <div v-if="canShowCoupon" class="pricing__coupon-wrapper">
            <div :class="['pricing__coupon', !isCouponValid && 'pricing__coupon--invalid']">
              <span>{{ coupon }}</span>
              <img
                @click.prevent="removeCoupon()"
                src="../assets/close-icon-coupon.png"
                class="pricing__coupon-close"
              />
            </div>
            <div
              :class="['pricing__coupon-label', !isCouponValid && 'pricing__coupon-label--invalid']"
            >
              {{ couponLabel }}
            </div>
          </div>
          <div
            :class="[
              'pricing__promo-input-wrapper',
              canShowCoupon && 'pricing__promo-input-wrapper--hide',
            ]"
          >
            <input
              class="ine-input pricing__promo-input"
              type="text"
              placeholder="Enter Promo Code"
              v-model="coupon"
            />
            <vue-loaders
              v-if="!canShowCoupon && couponSpinner"
              class="pricing__promo-input-spinner"
              name="line-spin-fade-loader"
              color="#dfe1e8"
              scale=".3"
            ></vue-loaders>
          </div>
          <button
            type="submit"
            :class="['btn', 'btn--primary', 'pricing__btn', canShowCoupon && 'pricing__btn--hide']"
            :disabled="!coupon"
            @click="handleCoupon"
          >
            Apply
          </button>
        </form>
        <p class="pricing__type">Total</p>
        <div class="pricing__total-group">
          <div class="pricing__line">
            <p class="pricing__label">subtotal</p>
            <p class="pricing__value">
              <span>{{ currencySymbol }}</span>
              <span>{{ subtotal }}</span>
            </p>
          </div>
          <div class="pricing__line">
            <p class="pricing__label">discount</p>
            <p class="pricing__value">
              <span>{{ currencySymbol }}</span>
              <span>{{ discount }}</span>
            </p>
          </div>
          <div class="pricing__line">
            <p class="pricing__label">tax</p>
            <p class="pricing__value">
              <span>{{
                taxes > 0 || isFreeTrial ? `${currencySymbol}${taxes}` : 'Not Included'
              }}</span>
            </p>
          </div>
          <div class="pricing__total">
            <p class="pricing__label pricing__label--total">
              {{ taxes > 0 || isFreeTrial ? 'Total due today' : 'Total before tax' }}
            </p>
            <p class="pricing__value pricing__value--total">
              <span>{{ currencySymbol }}</span>
              <span>{{ total }} USD</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import PricingListItem from '@/components/PricingListItem.vue'
import AddOns from '@/components/cart/AddOns.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'PricingList',
  components: {
    PricingListItem,
    AddOns,
  },
  data() {
    return {
      checkoutPricing: '',
      discount: '',
      subtotal: '',
      taxes: '',
      total: '',
      currencySymbol: '$',
      isPromoAvailable: false,
      currentCoupon: {},
      couponError: null,
      currentDiscount: 0,
      couponSpinner: false,
      certificationsList: [],
      certificationsInAddOns: [],
      planCodes: Object.values(CONSTANTS.PLANS),
    }
  },
  computed: {
    ...mapState({
      customCoupon: state => state.shoppingCart.coupon,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      paymentEntries: state => state.subscriptions.paymentEntries,
      availablePlans: state => state.products.availablePlans,
      availableBootcamps: state => state.products.availableBootcamps,
      availableCertifications: state => state.products.availableCertifications,
      selectedProductType: state => state.products.selectedProductType,
      promoField: state => state.subscriptions.promotionalField,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
    }),
    coupon: {
      set(value) {
        this.$store.dispatch('shoppingCart/updateCoupon', value)
      },
      get() {
        return this.customCoupon
      },
    },
    selectedPlanCode() {
      return this.selectedPlan?.merchant_id ?? ''
    },
    selectedPlanName() {
      const name = this.selectedPlan?.name ?? ''
      return name.replace('INE', '')
    },
    isPromo() {
      return this.promoField === CONSTANTS.PLANS.PROMO
    },
    isFreeTrial() {
      return !!this.selectedPlan?.plan_trial
    },
    isPremiumPlanSelected() {
      return this.selectedPlanCode === CONSTANTS.PLANS.PREMIUM
    },
    subscriptionsInCart() {
      const codes = this.availablePlans.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    certificationsInCart() {
      const codes = this.availableCertifications.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    bootcampsInCart() {
      const codes = this.availableBootcamps.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    showCouponAppliedPill() {
      return this.canShowCoupon && this.isCouponValid
    },
    isCouponValid() {
      return !this.couponError
    },
    canShowCoupon() {
      return !!this.currentCoupon && this.couponError !== null
    },
    couponLabel() {
      let couponLabel = null
      if (this.couponError === false && this.isCouponValid) {
        couponLabel = this.isFreeTrial
          ? `Nice! You're saving $${this.currentDiscount} on the first billing period after your trial ends.`
          : `Nice! You're saving $${this.currentDiscount}`
      } else {
        couponLabel = 'Code invalid'
      }
      return couponLabel
    },
    isCartFlowPage() {
      return ['payment', 'confirmation'].includes(this.$route.name)
    },
  },
  watch: {
    coupon: {
      inmediate: true,
      handler(value) {
        if (!value && !value.length) {
          this.setupPricing()
        }
      },
    },
    shoppingCart(items) {
      this.setupPricing()
    },
    paymentEntries: {
      deep: true,
      handler(paymentEntries) {
        const { country, zip } = paymentEntries
        this.checkoutPricing
          .address({
            country,
            postal_code: zip,
          })
          .catch(err => this.handleError(err))
          .done()
      },
    },
    certificationsInCart() {
      this.setCertificationsList()
    },
  },
  methods: {
    ...mapActions({
      addProductToShoppingCart: 'shoppingCart/addProductToShoppingCart',
      removeProductFromShoppingCart: 'shoppingCart/removeProductFromShoppingCart',
    }),
    removeCoupon() {
      this.coupon = ''
      this.currentCoupon = {}
    },
    async handleCouponLabel() {
      const coupon_data = await this.checkoutPricing?.items?.coupon
      if (!!coupon_data && !this.couponError) this.couponError = false
    },
    async handleCoupon() {
      if (!this.checkoutPricing) return
      this.couponError = false
      this.couponSpinner = true
      await this.checkoutPricing
        .coupon(this.customCoupon)
        .catch(error => {
          this.handleError(error)
          this.couponError = true
        })
        .done(() => (this.couponSpinner = false))
    },
    removeSelectedProduct(product) {
      const exists = this.shoppingCart.find(i => i.id === product.id)
      if (exists) {
        this.removeProductFromShoppingCart(exists)
      }
      if (this.shoppingCart.length > 0) {
        this.setupPricing()
      }
    },
    handlePricing(prices) {
      const {
        currency: { symbol },
        now: { discount, subscriptions, adjustments, taxes, total },
      } = prices
      const subtotal = (Number(subscriptions) + Number(adjustments)).toFixed(2).toString()
      if (symbol && symbol.length) {
        this.currencySymbol = symbol
      }
      const cartHasAddons = this.shoppingCart.filter(item => item.addOnChecked)
      if (this.isFreeTrial) {
        this.discount = '0.00'
        this.subtotal = cartHasAddons.length > 0 ? adjustments : '0.00'
        this.taxes = '0.00'
        this.total = cartHasAddons.length > 0 ? adjustments : '0.00'
        const freeTrialTotal = cartHasAddons.length > 0 ? adjustments : '0.00'
        this.$store.dispatch('subscriptions/updateCheckoutDiscount', freeTrialTotal)
        this.$store.dispatch('subscriptions/updateCheckoutTaxes', freeTrialTotal)
        this.$store.dispatch('subscriptions/updateCheckoutTotal', freeTrialTotal)
      } else {
        this.discount = discount
        this.subtotal = subtotal
        this.taxes = taxes
        this.total = total
        this.$store.dispatch('subscriptions/updateCheckoutDiscount', discount)
        this.$store.dispatch('subscriptions/updateCheckoutTaxes', taxes)
        this.$store.dispatch('subscriptions/updateCheckoutTotal', total)
      }
      this.currentDiscount = discount
      this.handleCouponLabel()
    },
    handleError(error) {
      console.error('handleError', error)
    },
    createSubscription() {
      const { country, zip: postal_code } = this.paymentEntries
      const subscription = window.recurly.Pricing.Subscription().plan(this.selectedPlanCode)
      return this.checkoutPricing
        .subscription(subscription.catch(error => this.handleError(error)).done())
        .address({ country, postal_code })
        .coupon(this.customCoupon)
    },
    addSingleProduct(item) {
      return this.checkoutPricing.adjustment({
        id: '1',
        itemCode: item.merchant_id,
        quantity: 1,
        taxExempt: false,
      })
    },
    calculatePricing() {
      if (this.shoppingCart && this.shoppingCart.length) {
        this.shoppingCart.map(item => {
          if (item.merchant_id && this.planCodes.includes(item.merchant_id)) {
            this.createSubscription()
              .catch(error => this.handleError(error))
              .done()
          } else {
            this.addSingleProduct(item)
              .catch(error => this.handleError(error))
              .done()
          }
        })
      }
    },
    setupPricing() {
      this.couponError = null
      this.currentCoupon = this.coupon
      this.currentDiscount = 0
      this.checkoutPricing = window.recurly.Pricing.Checkout()
      this.checkoutPricing.on('change', price => this.handlePricing(price))
      this.calculatePricing()
    },
    setCertificationsList() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      const addOnsItemsId = this.certificationsInAddOns.map(item => item.merchant_id)
      this.certificationsList = this.availableCertifications.filter(item => {
        if (this.selectedPlanCode === PLANS.ANNUAL && item.merchant_id === VOUCHERS.ICCA) {
          this.removeSelectedProduct(item)
          return false
        }
        return this.isCartFlowPage
          ? itemsId.includes(item.merchant_id) && !item.addOn
          : itemsId.includes(item.merchant_id) && !addOnsItemsId.includes(item.merchant_id)
      })
    },
    setCertificationsInAddOns() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      this.certificationsInAddOns = this.availableCertifications
        .filter(item => {
          item.included = false
          switch (item.merchant_id) {
            case VOUCHERS.ICCA:
              if ([PLANS.ANNUAL].includes(this.selectedPlanCode)) item.included = true
              break
            case VOUCHERS.EJPT_V2:
              if (
                [PLANS.ANNUAL, PLANS.MONTHLY_EJPTV2, PLANS.QUARTERLY_EJPTV2].includes(
                  this.selectedPlanCode
                )
              )
                item.included = true
              break
            default:
              break
          }
          if (!this.isCartFlowPage) item.addOnChecked = false
          // if user has a freeTrial subscription on cart should not be able to buy VOUCHERS.EJPT_V2
          if (this.isFreeTrial && [PLANS.PREMIUM, PLANS.MONTHLY].includes(this.selectedPlanCode)) {
            itemsId.push(VOUCHERS.EJPT_V2)
          }
          return this.isCartFlowPage
            ? !itemsId.includes(item.merchant_id) || item.addOn
            : !itemsId.includes(item.merchant_id)
        })
        .reverse()
    },
  },
  created() {
    this.isPromoAvailable = this.coupon.length > 0
    this.setupPricing()
    this.setCertificationsList()
    this.setCertificationsInAddOns()
    setTimeout(() => {
      this.couponSpinner = true
      this.checkoutPricing
        .coupon(this.coupon)
        .catch(error => {
          this.handleError(error)
          this.couponError = true
        })
        .done(() => (this.couponSpinner = false))
    }, 500)
  },
}
</script>

<style lang="scss">
.pricing {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  &__title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 20px 0;
    @include tablet {
      margin: 0 0 40px 0;
    }
  }
  &__title {
    color: var(--white);
    font-family: var(--font-regular);
    font-weight: 450;
    font-size: rem(24);
    line-height: 32px;
    margin: 0;
  }
  &__count-badge {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 9px 14px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    text-transform: uppercase;
    font-size: rem(12);
    line-height: 14px;
    font-family: var(--font-regular);
  }
  &__breakdown {
    padding: 0;
  }
  &__type {
    font-family: var(--font-regular);
    color: var(--white);
    font-size: rem(20px);
    line-height: 26px;
    text-align: left;
    font-weight: 700;
    margin-bottom: 10px;
  }
  &__prices-wrapper {
    position: relative;
    margin: 20px 0 0 0;
    @include tablet {
      margin: 50px 0 0 0;
      margin: 0;
    }
  }
  &__line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin: 0 0 15px 0;
    &:last-child {
      margin: 0;
    }
  }
  &__label {
    font-family: var(--font-regular);
    text-transform: capitalize;
    font-size: rem(16);
    margin: 0;
    &--total {
      color: var(--white);
      text-transform: none;
      font-size: rem(18);
    }
  }
  &__value {
    font-family: var(--font-regular);
    font-size: rem(16);
    margin: 0;
    &--total {
      color: var(--white);
      text-transform: none;
      font-size: rem(16);
    }
  }
  &__total {
    font-weight: 500;
    font-size: rem(18px);
    line-height: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-top: 1px solid #444444;
    padding-top: 5px;
  }
  &__promo {
    position: relative;
    font-family: var(--font-regular);
    outline: none;
    border: none;
    margin: 12.5px auto;
    padding: 0;
    width: 100%;
    display: flex;
    align-items: center;
    &-input {
      height: 46px;
      width: 100%;
      &-wrapper {
        width: 100%;
        &--hide {
          display: none;
        }
      }
      &-spinner {
        position: absolute;
        right: 100px;
        bottom: -3px;
      }
    }
  }
  &__input {
    background: #fff;
    padding: 5px;
    margin: 0;
    width: 100%;
    border-radius: 5px;
    font-size: 1rem;
    border: 1px solid var(--faded-gray);
    opacity: 0.7;
    font-family: var(--font-regular);
    &:focus {
      opacity: 1;
    }
  }
  &__btn {
    margin-left: 15px;
    &--hide {
      visibility: hidden;
    }
  }
  &__total-group {
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 10px;
    padding: 15px;
    color: var(--white);
  }
  &__coupon {
    height: 26px;
    padding: 5px 7px 5px 10px;
    color: var(--white);
    gap: 10px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--purple);

    &-wrapper {
      display: flex;
      align-items: center;
      width: 100%;
      height: 26px;
      min-width: fit-content;
    }
    &-label {
      color: var(--purple-pale);
      font-size: rem(14);
      line-height: 20px;
      margin-left: 10px;
      &--invalid {
        color: var(--performance-neg);
      }
    }
    &-close {
      cursor: pointer;
      width: 10px;
      height: 10px;
    }
    &--invalid {
      background: var(--performance-neg);
    }
  }
  &__coupon-pill-wrapper {
    position: absolute;
    top: 0;
    right: 0;
  }
  &__products {
    &-section {
      &--certifications {
        margin-top: 10px;
        display: inline-grid;
        grid-gap: 10px;
        width: 100%;
        .pricing-list-item {
          &__wrapper {
            height: auto;
            align-items: center;
          }
          &__btn {
            align-self: auto;
          }
        }
      }
    }
  }
}
</style>
